import React, { useState, useEffect, useCallback } from 'react';
import { ImageCard } from './ImageCard';
import { listGalleryImages, uploadPortfolioImage, updatePortfolioImage, deletePortfolioImage, persistImageOrder, GALLERY } from '../../lib/portfolio';

export const GalleryManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [isTall, setIsTall] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setImages(await listGalleryImages());
    } catch (err) {
      setStatus('Kunde inte hämta bilder: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!files.length) return;
    setUploading(true);
    setStatus('');
    let ok = 0;
    try {
      for (const file of files) {
        await uploadPortfolioImage(file, { category: GALLERY, isTall });
        ok += 1;
      }
      setStatus(`✓ ${ok} bild${ok === 1 ? '' : 'er'} tillagd${ok === 1 ? '' : 'a'}`);
      setFiles([]);
      setIsTall(false);
      e.target.reset();
      await load();
    } catch (err) {
      setStatus(`Något gick fel efter ${ok} bild(er): ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (image) => {
    if (!window.confirm('Ta bort denna bild permanent?')) return;
    await deletePortfolioImage(image);
    setImages((imgs) => imgs.filter((i) => i.id !== image.id));
  };

  const move = async (image, dir) => {
    const idx = images.findIndex((i) => i.id === image.id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= images.length) return;
    const next = [...images];
    [next[idx], next[j]] = [next[j], next[idx]];
    setImages(next);
    await persistImageOrder(next);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-cv-panel border border-cv-border" style={{ borderRadius: 'var(--cv-radius)', padding: 20 }}>
        <p className="m-0 text-sm text-cv-muted leading-[1.7]">
          Här styr du bildrutan <strong>"Senaste projekt"</strong> längst ned på startsidan. Lägg till nya bilder,
          markera enstaka som "stor" för ett luftigare rutnät, och ta bort det du inte vill visa.
        </p>
      </div>

      <form onSubmit={upload} className="bg-cv-base border border-cv-border flex flex-col gap-4" style={{ borderRadius: 'var(--cv-radius)', padding: 24 }}>
        <h3 className="m-0 font-serif font-medium text-[20px] text-cv-ink">Lägg till bilder</h3>
        <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} className="text-sm text-cv-muted" />
        <label className="flex items-center gap-2 text-sm text-cv-muted cursor-pointer">
          <input type="checkbox" checked={isTall} onChange={(e) => setIsTall(e.target.checked)} />
          Visa de här som stora (höga) bilder
        </label>
        <button type="submit" disabled={uploading || !files.length} className="w-fit bg-cv-ink text-cv-base border-none px-6 py-3 text-[13px] tracking-[0.14em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors disabled:opacity-60">
          {uploading ? 'Laddar upp…' : `Lägg till${files.length ? ` (${files.length})` : ''}`}
        </button>
        {status && <p className="m-0 text-sm text-cv-ink2">{status}</p>}
      </form>

      {loading ? (
        <p className="text-cv-muted">Laddar bilder…</p>
      ) : images.length === 0 ? (
        <p className="text-cv-muted text-sm leading-[1.7]">
          Inga bilder i databasen ännu. Tills du lägger till egna (eller kör seed-scriptet) visas ett standardgalleri på startsidan.
        </p>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))' }}>
          {images.map((img, i) => (
            <ImageCard
              key={img.id}
              image={img}
              showTall
              onSave={updatePortfolioImage}
              onDelete={remove}
              onMoveUp={(im) => move(im, -1)}
              onMoveDown={(im) => move(im, 1)}
              canMoveUp={i > 0}
              canMoveDown={i < images.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
